import {
  type FC,
  type ReactNode,
  type Ref,
  useMemo,
  useRef,
  useState,
} from "react";
import { AnimatePresence } from "motion/react";
import { useTranslation } from "react-i18next";
import { debounce, mergeClasses } from "@huub-web/utils";

import type { FormInputProps } from "../FormInput";
import {
  useDynamicInputValidation,
  type DynamicInputValidationResult,
} from "../../../hooks/useDynamicInputValidation";
import { useMounted } from "../../../hooks/useMounted";
import { useKeys } from "../../../hooks/useKeys";
import type { FieldConstraints } from "../../../types";
import type { BaseInputFieldProps } from "../../BaseInputField";
import type { SelectProps } from "../../Select";
import {
  FormInputPopupTypeOptions,
  type FormInputType,
  type DataFieldValue,
} from "../../../types";
import { FormInputError } from "../../FormInputError";
import {
  FormInputDisplayConfiguration,
  InputConfiguration,
  type InputDisplayConfiguration,
  ToggleInputDisplayConfiguration,
} from "../../../services/inputs";
import { mergeRefs } from "../../../utils/mergeRefs";

import { fieldStyle } from "./FormInputField.css";

export type FormInputFieldProps = Pick<
  FormInputProps,
  | "id"
  | "label"
  | "value"
  | "selectOptions"
  | "selectSearch"
  | "error"
  | "disabled"
  | "success"
  | "highlight"
  | "placeholder"
  | "required"
  | "info"
> & {
  fieldKey?: string;
  className?: string;
  closable?: boolean;
  display?: ReactNode;
  isInvalidType: boolean;
  active?: boolean;
  autoFocus?: boolean;
  bounding?: DOMRect;
  variant?: BaseInputFieldProps["variant"];
  type: FormInputType;
  props?: InputDisplayConfiguration["props"];
  /**
   * Dynamic validation constraints from backend json_schema_extra.
   */
  constraints?: FieldConstraints;
  onClick?: () => void;
  onOutsideClick?: () => void;
  onBlur?: () => void;
  onClose?: () => void;
  onValidation?: (result: DynamicInputValidationResult) => void;
  onChange?: (
    value: DataFieldValue,
    validation: DynamicInputValidationResult,
  ) => void;
};

/**
 * FormInputField component is a form input field with a label and a value display.
 */
export const FormInputField: FC<
  FormInputFieldProps & { ref?: Ref<HTMLDivElement> }
> = ({
  id,
  fieldKey = "__missing__fieldKey__",
  label,
  value,
  required,
  closable = false,
  type = "string",
  selectOptions,
  variant = "default",
  autoFocus = true,
  active = true,
  disabled = false,
  success = false,
  highlight = false,
  display,
  placeholder,
  info,
  error: errorProp,
  props: propsProp = {},
  constraints,
  selectSearch = false,
  className,
  onClick,
  onBlur,
  onChange: onChangeProp,
  onClose,
  onValidation,
  onOutsideClick,
  ref,
}) => {
  const { t } = useTranslation();
  const { isMountedRef } = useMounted();
  const internalRef = useRef<HTMLDivElement>(null);

  // console.log("[FormInputField] render!");

  if (closable) {
    // biome-ignore lint/correctness/useHookAtTopLevel: intentional conditional — closable is static per mount
    useKeys(["Escape"], () => {
      if (isMountedRef.current) {
        // console.log(
        //   "[FormInputField] useKeys calling onClose",
        //   isMountedRef.current,
        // );
        onClose?.();
      }
    });

    // if (!FormInputPopupTypeOptions.includes(type)) {
    //   useKeys(["Enter"], () => {
    //     if (isMountedRef.current) {
    //       onClose?.();
    //     }
    //   });
    // }
  }

  const Component = InputConfiguration?.[type]?.field as FC<
    Omit<BaseInputFieldProps, "defaultValue"> & {
      value: string;
      inputType?: string;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      ref?: Ref<any>;
    }
  >;
  const DisplayConfiguration: InputDisplayConfiguration =
    variant === "default"
      ? FormInputDisplayConfiguration[type]
      : ToggleInputDisplayConfiguration[type];

  if (!Component) {
    console.error(`Component for type "${type}" not found.`);
    return null;
  }
  if (!DisplayConfiguration) {
    throw new Error(`Display configuration for type "${type}" not found.`);
  }

  const [validationResult, setValidationResult] =
    // biome-ignore lint/correctness/useHookAtTopLevel: hooks after early return guard — component unmounts on guard, so hook order is stable
    useState<DynamicInputValidationResult | null>(null);
  // biome-ignore lint/correctness/useHookAtTopLevel: hooks after early return guard — component unmounts on guard, so hook order is stable
  const setValidationResultDebounced = useRef(
    debounce(setValidationResult, 500),
  );
  /**
   * Dynamic validation: Local/inline validation that runs on every change.
   * Provides HTML attributes (maxLength, min, max) and validates as user types.
   * Does NOT register with FormContext - only shows errors during input.
   */
  const { validationProps: dynamicValidationProps, validate } =
    // biome-ignore lint/correctness/useHookAtTopLevel: hooks after early return guard — component unmounts on guard, so hook order is stable
    useDynamicInputValidation(type, {
      required,
      constraints,
    });

  /**
   * Form validation: Registers field with FormContext for submit-time validation.
   * When form submits, FormContext calls all registered validators.
   * This is what makes required fields show errors on submit.
   */
  // biome-ignore lint/correctness/useHookAtTopLevel: hooks after early return guard — component unmounts on guard, so hook order is stable
  const validation = useMemo(() => {
    const rules: string[] = [];
    if (required) {
      rules.push("required");
    }
    return rules.length > 0 ? rules : undefined;
  }, [required]);

  // biome-ignore lint/correctness/useHookAtTopLevel: hooks after early return guard — component unmounts on guard, so hook order is stable
  const onChange = useMemo<(value: DataFieldValue) => void>(
    () => (value: DataFieldValue) => {
      const validation = validate(value);
      setValidationResultDebounced.current(validation);
      onValidation?.(validation);

      if (InputConfiguration[type].onChange) {
        InputConfiguration[type].onChange({
          value,
          onChange: onChangeProp,
          onClose,
          validation,
        });
      } else {
        onChangeProp?.(value, validation);
      }
    },
    [validate, onChangeProp, onValidation, onClose, type],
  );

  // Validation errors take priority, then errorProp
  const error = validationResult?.success === false || !!errorProp;

  // biome-ignore lint/correctness/useHookAtTopLevel: hooks after early return guard — component unmounts on guard, so hook order is stable
  const selectProps = useMemo<SelectProps>(() => {
    const props: SelectProps = {};

    if (selectSearch) {
      props.search = true;
    }

    if (["boolean"].includes(type)) {
      props.options = [
        { label: t("general.yes"), value: true },
        { label: t("general.no"), value: false },
      ];
    }

    if (["select", "multiSelect", "radio", "checkboxes"].includes(type)) {
      props.options = selectOptions?.map(({ label, value }) => ({
        label,
        value: value?.toString() ?? "",
      }));
    }

    if (onOutsideClick && FormInputPopupTypeOptions.includes(type)) {
      props.onOutsideClick = onOutsideClick;
    }

    return props;
  }, [type, selectOptions, selectSearch, onOutsideClick, t]);

  /**
   * Compute the value to pass to the component
   */
  const computedValue = (value ?? "") as string;

  /**
   * Compose parent onBlur with InputConfiguration onBlur.
   * Config onBlur receives the current value and internal onChange
   * (which runs validation automatically).
   */
  const handleBlur = () => {
    onBlur?.();
    // onChange here is the internal onChange (line 195 useMemo) which
    // runs validation automatically — not the raw onChangeProp.
    InputConfiguration[type].onBlur?.({
      value: computedValue,
      onChange,
    });
  };

  return (
    <>
      <Component
        ref={mergeRefs(ref, internalRef)}
        id={id}
        name={fieldKey}
        className={mergeClasses(fieldStyle, className)}
        leading={DisplayConfiguration?.leading}
        trailing={DisplayConfiguration?.trailing}
        placeholder={
          placeholder
            ? placeholder
            : DisplayConfiguration?.placeholderKey
              ? t(DisplayConfiguration.placeholderKey)
              : undefined
        }
        variant={variant}
        label={label}
        autoFocus={autoFocus}
        active={active}
        error={error}
        success={success}
        display={display}
        info={info}
        disabled={disabled}
        highlight={highlight}
        onChange={onChange}
        onClick={onClick}
        onBlur={handleBlur}
        style={{ maxWidth: DisplayConfiguration?.maxWidth }}
        width="stretch"
        required={required}
        validation={validation}
        {...dynamicValidationProps}
        {...InputConfiguration[type].props}
        {...propsProp}
        {...selectProps}
        inputType={type}
        data-field-key={fieldKey}
        value={computedValue}
      />
      {/* Only show error in FormInputField for toggle variant (floating).
            For default variant, FormRow handles error display. */}
      <AnimatePresence>
        {variant === "toggle" &&
          (!!errorProp || validationResult?.success === false) && (
            <FormInputError
              targetRef={internalRef}
              message={errorProp ?? validationResult?.errors?.[0]}
              floating
              placement="bottom"
            />
          )}
      </AnimatePresence>
    </>
  );
};
